import { useEffect, useRef } from 'react';
import { isInDevelopment } from '../ga4/constants';

/**
 * A React hook that replaces the global timer functions (setTimeout, setInterval)
 * with versions that are powered by a Web Worker. This ensures timers continue
 * to run accurately even when the browser tab is in the background.
 *
 * This hook should be called ONCE in the root component of thr application (e.g., App.js).
 */
const useTabSafeTimers = () => {
  // A ref to ensure initialization happens only once.
  const isInitialized = useRef(false);

  useEffect(() => {
    // Prevent re-initialization on re-renders.
    if (isInitialized.current) return;
    isInitialized.current = true;

    const styles = {
      header: 'color: #10b981; font-weight: bold; font-size: 14px;',
      section: 'color: #3b82f6; font-weight: bold;',
      label: 'color: #f59e0b; font-weight: bold;',
      muted: 'color: #9ca3af;'
    };

    if (isInDevelopment) {
      console.log('%c⚡ DEV/STG Logs - Tab-Safe Timers Report [ START ]', styles.header);
      console.log('%cInitializing Web Worker for background timers...', styles.section);
    }

    // Store the original timer functions so we can restore them on cleanup.
    const native = {
      setInterval: window.setInterval.bind(window),
      setTimeout: window.setTimeout.bind(window),
      clearInterval: window.clearInterval.bind(window),
      clearTimeout: window.clearTimeout.bind(window)
    };

    // Create the Web Worker instance. The worker script must be in the `public` folder.
    let worker;
    try {
      worker = new Worker('/timer.worker.js');
    } catch (error) {
      if (isInDevelopment) {
        console.log('%c🚨 DEV/STG Logs - Web Worker Failed to Initialize', styles.header);
        console.error(
          'Could not create Web Worker. Ensure timer.worker.js is in the public folder.',
          error
        );
        console.log('%c🚨 DEV/STG Logs - Web Worker Failed to Initialize [  END  ]', styles.header);
      }
      // Fallback to native timers if worker fails
      isInitialized.current = false; // Allow re-initialization if needed
      return;
    }

    // Maps to store the callbacks for active timers. The key is a unique ID.
    const callbacks = new Map();
    let idCounter = 0;

    // Handle messages received from the worker.
    worker.onmessage = (e) => {
      const { type, id } = e.data;
      if (type === 'tick') {
        const callbackData = callbacks.get(id);
        if (callbackData) {
          try {
            // Execute the original callback with its arguments.
            callbackData.callback(...callbackData.args);
          } catch (error) {
            console.error('Error in worker timer callback:', error);
          }
          // If it's a timeout, it only runs once, so we remove it.
          if (callbackData.type === 'timeout') {
            callbacks.delete(id);
          }
        }
      }
    };

    worker.onerror = (error) => {
      if (isInDevelopment) {
        console.log('%c🚨 DEV/STG Logs - Web Worker Error', styles.header);
        console.error('Error in Web Worker:', error.message, error);
        console.log('%c🚨 DEV/STG Logs - Web Worker Error [  END  ]', styles.header);
      }
    };

    // --- OVERRIDE GLOBAL FUNCTIONS ---

    window.setInterval = (callback, delay, ...args) => {
      const id = ++idCounter;
      callbacks.set(id, { type: 'interval', callback, args, delay });
      worker.postMessage({ command: 'setInterval', id, delay });

      return id;
    };

    window.setTimeout = (callback, delay, ...args) => {
      const id = ++idCounter;
      callbacks.set(id, { type: 'timeout', callback, args, delay });
      worker.postMessage({ command: 'setTimeout', id, delay });

      return id;
    };

    window.clearInterval = (id) => {
      if (callbacks.has(id)) {
        callbacks.delete(id);
        worker.postMessage({ command: 'clearInterval', id });
      } else {
        native.clearInterval(id);
      }
    };

    window.clearTimeout = (id) => {
      if (callbacks.has(id)) {
        callbacks.delete(id);
        worker.postMessage({ command: 'clearTimeout', id });
      } else {
        native.clearTimeout(id);
      }
    };

    if (isInDevelopment) {
      console.log('%cInitialization Complete. Native timers have been overridden.', styles.muted);
      console.log('%c⚡ DEV/STG Logs - Tab-Safe Timers Report [  END  ]', styles.header);
      console.log('');
    }

    // --- CLEANUP ---
    return () => {
      if (isInDevelopment) {
        console.log('%c🔄 DEV/STG Logs - Cleanup Report [ START ]', styles.header);
        console.log('%cRestoring native timers and terminating worker...', styles.section);
      }

      window.setInterval = native.setInterval;
      window.setTimeout = native.setTimeout;
      window.clearInterval = native.clearInterval;
      window.clearTimeout = native.clearTimeout;

      worker.terminate();
      isInitialized.current = false;

      if (isInDevelopment) {
        console.log('%cCleanup Complete. Native timers restored.', styles.muted);
        console.log('%c🔄 DEV/STG Logs - Cleanup Report [  END  ]', styles.header);
        console.log('');
      }
    };
  }, []);
};

export default useTabSafeTimers;
